import React, { useState, useEffect } from "react";
import { Modal, Descriptions, Tag, Button, Space, Divider, Alert, Select, message, Tabs, Card, Empty, Timeline, Typography, Row, Col, Drawer, Badge, notification, Tooltip } from "antd";
import { mockTransactions } from "../mockData"; // Adjust path as needed
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import TransactionActionForm from "./TransactionActionForm"; // Import the form
import { RobotOutlined, CheckOutlined, CloseOutlined, InfoCircleOutlined, MailOutlined, WarningOutlined } from "@ant-design/icons";

dayjs.extend(utc);
const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;

// 添加Agent相关状态常量
const TRANSACTION_STATUS = {
    APPROVED: "APPROVED",
    PENDING: "PENDING",
    IN_PROCESS: "IN_PROCESS",
    REJECTED: "REJECTED",
    CANCELLED: "CANCELLED",
    CONFIRMED: "CONFIRMED",
    COMPLETED: "COMPLETED",
    INFO: "INFO",
    PROCESSING: "PROCESSING",
    DRAFT: "DRAFT",
    RECORDED: "RECORDED",
    WAITING_CONFIRM: "WAITING_CONFIRM", // 待人工确认的状态
    AGENT_PROCESSED: "AGENT_PROCESSED", // Agent处理过的状态
};

const TransactionModal = ({ visible, flight, onClose }) => {
    const [transactions, setTransactions] = useState([]);
    const [showActionForm, setShowActionForm] = useState(false);
    const [selectedTransactionType, setSelectedTransactionType] = useState(null); // e.g., 'FUEL', 'PERMIT'
    const [formInitialValues, setFormInitialValues] = useState({});
    const [activeTabKey, setActiveTabKey] = useState("overview");
    const [emailPreviewVisible, setEmailPreviewVisible] = useState(false);
    const [currentEmailId, setCurrentEmailId] = useState(null);
    const [agentConfirmVisible, setAgentConfirmVisible] = useState(false);
    const [currentAgentTransaction, setCurrentAgentTransaction] = useState(null);
    const [agentActionLoading, setAgentActionLoading] = useState(false);
    const [notificationApi, contextHolder] = notification.useNotification();
    const [messageApi, messageContextHolder] = message.useMessage();

    // Transaction Type Options (based on design doc & models)
    const transactionTypes = [
        { value: "FUEL", label: "燃油申请", icon: "⛽" },
        { value: "PERMIT", label: "航路许可", icon: "📝" },
        { value: "ACTIVATION", label: "航班激活", icon: "🚀" },
        { value: "MVT", label: "航班动态", icon: "📊" },
        { value: "CREW", label: "机组安排", icon: "👨‍✈️" },
        { value: "CATERING", label: "餐食服务", icon: "🍽️" },
        { value: "CUSTOMS", label: "海关申报", icon: "🛃" },
        { value: "SLOT", label: "时刻申请", icon: "⏱️" },
        { value: "HANDLING", label: "地面服务", icon: "🚚" },
    ];

    useEffect(() => {
        if (flight && visible) {
            // Simulate fetching transactions for the selected flight
            // 模拟数据中添加一些Agent处理的记录
            let flightTransactions = mockTransactions[flight.id] || [];

            // 添加模拟的Agent处理的事务（如果原始数据中没有）
            if (flight.id === 1 && flightTransactions.length > 0) {
                // 只给第一个航班添加模拟Agent数据，避免所有航班都添加
                const hasAgentTransactions = flightTransactions.some((t) => t.is_agent_action);
                if (!hasAgentTransactions) {
                    const agentTransactions = [
                        {
                            id: 10001,
                            record_type: "FUEL",
                            event_description: "[AI解析] 燃油申请已被批准，总量5000公斤",
                            status: TRANSACTION_STATUS.WAITING_CONFIRM,
                            created_at: dayjs().subtract(2, "hour").toISOString(),
                            operator: "AI助手",
                            related_email_id: 1001,
                            is_agent_action: true,
                            agent_confidence: 0.95,
                            agent_suggestion: "邮件确认燃油申请已被批准，总量5000公斤，建议将状态更新为已批准",
                        },
                        {
                            id: 10002,
                            record_type: "PERMIT",
                            event_description: "[AI检测] 许可申请被拒绝，需修改航路点",
                            status: TRANSACTION_STATUS.WAITING_CONFIRM,
                            created_at: dayjs().subtract(1, "hour").toISOString(),
                            operator: "AI助手",
                            related_email_id: 1002,
                            is_agent_action: true,
                            agent_confidence: 0.88,
                            agent_suggestion: "邮件表明许可申请被拒绝，原因是需要修改航路点。建议将状态更新为已拒绝并通知航班计划部门。",
                        },
                    ];
                    flightTransactions = [...flightTransactions, ...agentTransactions];
                }
            }

            setTransactions(flightTransactions);

            // Reset form state when modal opens or flight changes
            setShowActionForm(false);
            setSelectedTransactionType(null);
            setFormInitialValues({});
            setActiveTabKey("overview");
        } else {
            setTransactions([]); // Clear transactions when modal is closed or no flight
        }
    }, [flight, visible]);

    // 获取待确认的Agent事务数量
    const getPendingAgentCount = () => {
        return transactions.filter((t) => t.status === TRANSACTION_STATUS.WAITING_CONFIRM && t.is_agent_action).length;
    };

    // 处理Agent建议确认
    const handleAgentConfirm = (transaction) => {
        setCurrentAgentTransaction(transaction);
        setAgentConfirmVisible(true);
    };

    // 确认Agent建议
    const confirmAgentAction = async () => {
        if (!currentAgentTransaction) return;

        setAgentActionLoading(true);
        try {
            // 模拟API请求
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // 根据Agent建议更新状态
            let newStatus = currentAgentTransaction.status;
            const suggestion = currentAgentTransaction.agent_suggestion || "";

            if (suggestion.includes("已被批准") || suggestion.includes("批准")) {
                newStatus = TRANSACTION_STATUS.APPROVED;
            } else if (suggestion.includes("被拒绝") || suggestion.includes("拒绝")) {
                newStatus = TRANSACTION_STATUS.REJECTED;
            } else if (suggestion.includes("已完成") || suggestion.includes("完成")) {
                newStatus = TRANSACTION_STATUS.COMPLETED;
            }

            // 更新本地事务数据
            const updatedTransactions = transactions.map((item) => {
                if (item.id === currentAgentTransaction.id) {
                    return {
                        ...item,
                        status: newStatus,
                        agent_action_confirmed: true,
                        agent_action_confirmed_at: dayjs().toISOString(),
                        agent_action_confirmed_by: "当前用户",
                        event_description: `${item.event_description} [已确认]`,
                    };
                }
                return item;
            });

            setTransactions(updatedTransactions);
            messageApi.success("已确认AI助手的处理建议");
        } catch (error) {
            console.error("确认失败:", error);
            messageApi.error("确认失败，请重试");
        } finally {
            setAgentActionLoading(false);
            setAgentConfirmVisible(false);
            setCurrentAgentTransaction(null);
        }
    };

    // 拒绝Agent建议
    const rejectAgentAction = async () => {
        if (!currentAgentTransaction) return;

        setAgentActionLoading(true);
        try {
            // 模拟API请求
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // 更新本地事务数据，将状态重置
            const updatedTransactions = transactions.map((item) => {
                if (item.id === currentAgentTransaction.id) {
                    return {
                        ...item,
                        status: TRANSACTION_STATUS.PENDING, // 重置为待处理状态
                        agent_action_rejected: true,
                        agent_action_rejected_at: dayjs().toISOString(),
                        agent_action_rejected_by: "当前用户",
                        event_description: `${item.event_description} [已拒绝]`,
                    };
                }
                return item;
            });

            setTransactions(updatedTransactions);
            messageApi.info("已拒绝AI助手的处理建议");
        } catch (error) {
            console.error("拒绝失败:", error);
            messageApi.error("操作失败，请重试");
        } finally {
            setAgentActionLoading(false);
            setAgentConfirmVisible(false);
            setCurrentAgentTransaction(null);
        }
    };

    const handleInitiateTransaction = () => {
        if (!selectedTransactionType) {
            messageApi.warning("请先选择要发起的事务类型");
            return;
        }
        // Prepare initial values based on flight and transaction type
        setFormInitialValues({
            flightInfo: flight, // Pass the whole flight object
            transactionType: selectedTransactionType,
        });
        setShowActionForm(true);
        setActiveTabKey("newTransaction");
    };

    const handleFormCancel = () => {
        setShowActionForm(false);
        setSelectedTransactionType(null); // Reset selection
        setActiveTabKey("overview");
    };

    const handleFormSubmit = (formData) => {
        console.log("Simulating transaction submission:", formData);
        // In a real app: Send data to backend, update transaction list, show success message
        messageApi.success(`模拟发起 ${selectedTransactionType} 事务成功`);
        setShowActionForm(false);
        setSelectedTransactionType(null); // Reset selection
        setActiveTabKey("overview");

        // For demo, we can manually add a mock entry
        const newMockTransaction = {
            id: Date.now(), // Simple unique ID for demo
            record_type: selectedTransactionType,
            event_description: `[模拟] ${formData.subject || "新事务已发起"}`,
            status: "PENDING", // Default status
            created_at: dayjs().toISOString(),
            operator: "CurrentUser", // Replace with actual user
            related_email_id: null, // Or a mock ID if simulating email sending
            is_agent_action: false, // 用户创建，非Agent动作
        };
        setTransactions((prev) => [...prev, newMockTransaction]);
    };

    const getTransactionStatusColor = (status) => {
        switch (status) {
            case TRANSACTION_STATUS.COMPLETED:
            case TRANSACTION_STATUS.CONFIRMED:
            case TRANSACTION_STATUS.INFO:
            case TRANSACTION_STATUS.RECORDED:
            case TRANSACTION_STATUS.APPROVED:
                return "green";
            case TRANSACTION_STATUS.PENDING:
            case TRANSACTION_STATUS.DRAFT:
                return "blue";
            case TRANSACTION_STATUS.PROCESSING:
            case TRANSACTION_STATUS.IN_PROCESS:
            case TRANSACTION_STATUS.AGENT_PROCESSED:
                return "purple";
            case TRANSACTION_STATUS.CANCELLED:
            case TRANSACTION_STATUS.REJECTED:
                return "red";
            case TRANSACTION_STATUS.WAITING_CONFIRM:
                return "geekblue";
            default:
                return "default";
        }
    };

    const getStatusLabel = (status) => {
        // 状态标签本地化
        const statusMap = {
            [TRANSACTION_STATUS.APPROVED]: "已批准",
            [TRANSACTION_STATUS.PENDING]: "待处理",
            [TRANSACTION_STATUS.IN_PROCESS]: "处理中",
            [TRANSACTION_STATUS.REJECTED]: "已拒绝",
            [TRANSACTION_STATUS.CANCELLED]: "已取消",
            [TRANSACTION_STATUS.CONFIRMED]: "已确认",
            [TRANSACTION_STATUS.COMPLETED]: "已完成",
            [TRANSACTION_STATUS.INFO]: "信息记录",
            [TRANSACTION_STATUS.PROCESSING]: "处理中",
            [TRANSACTION_STATUS.DRAFT]: "草稿",
            [TRANSACTION_STATUS.RECORDED]: "已记录",
            [TRANSACTION_STATUS.WAITING_CONFIRM]: "待确认",
            [TRANSACTION_STATUS.AGENT_PROCESSED]: "AI已处理",
        };
        return statusMap[status] || status;
    };

    // 获取不同事务类型的最新状态
    const getTransactionsByType = () => {
        const result = {};
        transactionTypes.forEach((type) => {
            result[type.value] = {
                type: type.value,
                label: type.label,
                icon: type.icon,
                transactions: transactions.filter((t) => t.record_type === type.value),
                // 计算该类型的最新状态
                latestStatus: transactions.filter((t) => t.record_type === type.value).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.status || null,
                // 添加是否有需要确认的AI处理
                hasAgentConfirm: transactions.some((t) => t.record_type === type.value && t.status === TRANSACTION_STATUS.WAITING_CONFIRM && t.is_agent_action),
            };
        });
        return result;
    };

    const handleViewEmail = (emailId) => {
        setCurrentEmailId(emailId);
        setEmailPreviewVisible(true);
    };

    const renderEmailPreview = () => {
        // 模拟邮件数据
        const emailData = {
            id: currentEmailId,
            from: "operations@uas.aero",
            to: "operations@flymeta.com",
            subject: `Route Approval for Flight ${flight?.flight_number || "Unknown"} (${currentEmailId})`,
            date: dayjs().subtract(1, "day").format("YYYY-MM-DD HH:mm:ss"),
            content: `
Dear Operations Team,

Please find attached the route approval for flight ${flight?.flight_number} from ${flight?.departure_airport} to ${flight?.arrival_airport} on ${dayjs.utc(flight?.flight_date).format("YYYY-MM-DD")}.

All waypoints have been approved as requested. Please notify us of any changes to the flight plan at least 24 hours before departure.

Best regards,
UAS Flight Planning Team
            `,
        };

        return (
            <Drawer title={`邮件预览 #${currentEmailId}`} width={600} onClose={() => setEmailPreviewVisible(false)} open={emailPreviewVisible}>
                <Card size="small" bordered={false} style={{ marginBottom: "16px" }}>
                    <Descriptions column={1} size="small">
                        <Descriptions.Item label="发件人">{emailData.from}</Descriptions.Item>
                        <Descriptions.Item label="收件人">{emailData.to}</Descriptions.Item>
                        <Descriptions.Item label="主题">{emailData.subject}</Descriptions.Item>
                        <Descriptions.Item label="日期">{emailData.date}</Descriptions.Item>
                    </Descriptions>
                </Card>
                <Card title="邮件内容" size="small">
                    <div style={{ whiteSpace: "pre-line" }}>{emailData.content}</div>
                </Card>
                <Card
                    title="附件"
                    size="small"
                    style={{ marginTop: "16px" }}
                    extra={
                        <Button type="primary" size="small">
                            下载所有附件
                        </Button>
                    }
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Button icon="📄" type="link">
                            TRIP-BRIEF-{flight?.flight_number}-{dayjs.utc(flight?.flight_date).format("YYYYMMDD")}.pdf
                        </Button>
                        <Button icon="📄" type="link">
                            Flight-Plan-{flight?.flight_number}.pdf
                        </Button>
                    </div>
                </Card>
            </Drawer>
        );
    };

    const renderOverviewTab = () => {
        const transactionsByType = getTransactionsByType();

        return (
            <div>
                <Row gutter={[16, 16]}>
                    {transactionTypes.map((type) => {
                        const data = transactionsByType[type.value];
                        const hasTransactions = data.transactions.length > 0;
                        const latestTransaction = hasTransactions ? data.transactions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] : null;

                        return (
                            <Col span={8} key={type.value}>
                                <Card
                                    title={
                                        <Space>
                                            <span style={{ fontSize: "22px" }}>{type.icon}</span>
                                            <span>{type.label}</span>
                                            {latestTransaction && <Tag color={getTransactionStatusColor(latestTransaction.status)}>{getStatusLabel(latestTransaction.status)}</Tag>}
                                            {data.hasAgentConfirm && <Badge status="processing" text="待确认" />}
                                        </Space>
                                    }
                                    size="small"
                                    style={{ height: "100%", minHeight: "160px" }}
                                    extra={
                                        <Button
                                            type={hasTransactions ? "link" : "primary"}
                                            size="small"
                                            onClick={() => {
                                                if (hasTransactions) {
                                                    // 切换到该事务类型的详情tab
                                                    setActiveTabKey(type.value);
                                                } else {
                                                    // 发起新事务
                                                    setSelectedTransactionType(type.value);
                                                    handleInitiateTransaction();
                                                }
                                            }}
                                        >
                                            {hasTransactions ? "查看详情" : "发起"}
                                        </Button>
                                    }
                                >
                                    {hasTransactions ? (
                                        <div>
                                            <Text type="secondary">最近更新: {dayjs(latestTransaction.created_at).format("YYYY-MM-DD HH:mm")}</Text>
                                            <p style={{ marginTop: "8px", fontSize: "14px" }}>
                                                {latestTransaction.is_agent_action && (
                                                    <Tooltip title="由AI助手处理">
                                                        <RobotOutlined style={{ marginRight: 4, color: "#1890ff" }} />
                                                    </Tooltip>
                                                )}
                                                {latestTransaction.event_description}
                                            </p>
                                            {data.transactions.length > 1 && (
                                                <div style={{ marginTop: "8px" }}>
                                                    <Tag color="blue">共 {data.transactions.length} 条记录</Tag>
                                                    {data.hasAgentConfirm && (
                                                        <Tag color="geekblue" icon={<RobotOutlined />}>
                                                            有AI待确认
                                                        </Tag>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无相关事务" style={{ margin: "20px 0" }} />
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </div>
        );
    };

    const renderTransactionTypeTab = (type) => {
        const filteredTransactions = transactions.filter((t) => t.record_type === type);
        const typeInfo = transactionTypes.find((t) => t.value === type);

        if (filteredTransactions.length === 0) {
            return <Empty description={`暂无${typeInfo?.label}相关事务`} />;
        }

        // 按时间排序，最新的在前面
        const sortedTransactions = [...filteredTransactions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        // 检查是否有AI助手待确认项目
        const hasAgentConfirm = sortedTransactions.some((tx) => tx.status === TRANSACTION_STATUS.WAITING_CONFIRM && tx.is_agent_action);

        return (
            <div>
                {hasAgentConfirm && <Alert message="AI助手提醒" description={`有${sortedTransactions.filter((tx) => tx.status === TRANSACTION_STATUS.WAITING_CONFIRM && tx.is_agent_action).length}条事务需要您确认AI助手的处理结果`} type="info" showIcon icon={<RobotOutlined />} style={{ marginBottom: 16 }} />}

                <Card
                    title={
                        <Space>
                            <span style={{ fontSize: "24px" }}>{typeInfo?.icon}</span>
                            <Title level={4} style={{ margin: 0 }}>
                                {typeInfo?.label}事务记录
                            </Title>
                            <Tag color="blue">共 {filteredTransactions.length} 条记录</Tag>
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => {
                                    setSelectedTransactionType(type);
                                    handleInitiateTransaction();
                                }}
                            >
                                发起新{typeInfo?.label}
                            </Button>
                        </Space>
                    }
                    bordered={false}
                >
                    <Timeline
                        mode="left"
                        style={{ marginTop: "20px" }}
                        items={sortedTransactions.map((tx) => ({
                            color: getTransactionStatusColor(tx.status),
                            label: (
                                <div>
                                    <div style={{ fontWeight: "bold" }}>{dayjs(tx.created_at).format("YYYY-MM-DD")}</div>
                                    <div>{dayjs(tx.created_at).format("HH:mm:ss")}</div>
                                </div>
                            ),
                            dot: tx.is_agent_action ? <RobotOutlined /> : null,
                            children: (
                                <Card
                                    size="small"
                                    style={{
                                        marginBottom: "8px",
                                        borderLeft: tx.status === TRANSACTION_STATUS.WAITING_CONFIRM && tx.is_agent_action ? "3px solid #1890ff" : undefined,
                                    }}
                                >
                                    <div>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                            <Space>
                                                <Tag color={getTransactionStatusColor(tx.status)}>{getStatusLabel(tx.status)}</Tag>
                                                <Text strong>
                                                    {tx.is_agent_action && (
                                                        <Tooltip title="由AI助手处理">
                                                            <RobotOutlined style={{ marginRight: 4, color: "#1890ff" }} />
                                                        </Tooltip>
                                                    )}
                                                    {tx.event_description}
                                                </Text>
                                                {tx.agent_confidence && (
                                                    <Tooltip title="AI助手对分析结果的置信度">
                                                        <Tag color="blue">置信度: {(tx.agent_confidence * 100).toFixed(0)}%</Tag>
                                                    </Tooltip>
                                                )}
                                            </Space>

                                            <Space>
                                                {tx.status === TRANSACTION_STATUS.WAITING_CONFIRM && tx.is_agent_action ? (
                                                    <>
                                                        <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => handleAgentConfirm(tx)}>
                                                            确认
                                                        </Button>
                                                    </>
                                                ) : null}

                                                {tx.related_email_id && (
                                                    <Button type="primary" size="small" icon={<MailOutlined />} onClick={() => handleViewEmail(tx.related_email_id)}>
                                                        查看邮件 #{tx.related_email_id}
                                                    </Button>
                                                )}
                                            </Space>
                                        </div>

                                        {tx.agent_suggestion && <Alert message="AI助手建议" description={tx.agent_suggestion} type="info" style={{ marginBottom: 8 }} showIcon icon={<RobotOutlined />} />}

                                        <div style={{ color: "#888" }}>
                                            <Space split={<Divider type="vertical" />}>
                                                <span>操作人: {tx.operator || "-"}</span>
                                                {tx.related_email_id && <span>邮件ID: {tx.related_email_id}</span>}
                                                <span>时间: {dayjs(tx.created_at).format("YYYY-MM-DD HH:mm:ss")}</span>
                                            </Space>
                                        </div>
                                    </div>
                                </Card>
                            ),
                        }))}
                    />
                </Card>
            </div>
        );
    };

    if (!flight) return null;

    const modalTitle = `航班 ${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport}) - 事务处理`;

    return (
        <>
            {contextHolder}
            {messageContextHolder}
            <Modal title={modalTitle} open={visible} onCancel={onClose} footer={null} className="large-modal" style={{ top: 20 }} destroyOnHidden>
                <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="航班日期">{dayjs.utc(flight.flight_date).format("YYYY-MM-DD")}</Descriptions.Item>
                    <Descriptions.Item label="飞机号">{flight.aircraft_reg || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="计划时间 (UTC)">{`${dayjs.utc(flight.std).format("HH:mm")} - ${dayjs.utc(flight.sta).format("HH:mm")}`}</Descriptions.Item>
                    <Descriptions.Item label="状态">
                        <Tag color={getStatusBadgeType(flight.status)}>{flight.status}</Tag>
                    </Descriptions.Item>
                </Descriptions>

                {getPendingAgentCount() > 0 && (
                    <Alert
                        message={
                            <Space>
                                <RobotOutlined />
                                <span>AI助手提醒</span>
                                <Badge count={getPendingAgentCount()} style={{ backgroundColor: "#1890ff" }} />
                            </Space>
                        }
                        description={`AI助手检测到 ${getPendingAgentCount()} 条与该航班相关的邮件需要您确认处理结果`}
                        type="info"
                        style={{ marginBottom: 16 }}
                        action={
                            <Button
                                size="small"
                                type="primary"
                                onClick={() => {
                                    // 跳转到有待确认事务的Tab页
                                    const typesWithAgentConfirm = transactionTypes.find((type) => {
                                        const data = getTransactionsByType()[type.value];
                                        return data && data.hasAgentConfirm;
                                    });
                                    if (typesWithAgentConfirm) {
                                        setActiveTabKey(typesWithAgentConfirm.value);
                                    }
                                }}
                            >
                                查看待确认事务
                            </Button>
                        }
                    />
                )}

                <Tabs
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    items={[
                        {
                            key: "overview",
                            label: "概览",
                            children: renderOverviewTab(),
                        },
                        ...transactionTypes.map((type) => {
                            const typeData = getTransactionsByType()[type.value];
                            const count = transactions.filter((t) => t.record_type === type.value).length;
                            const hasAgentConfirm = typeData && typeData.hasAgentConfirm;

                            return {
                                key: type.value,
                                label: (
                                    <span>
                                        {type.icon} {type.label}
                                        {count > 0 && <Tag style={{ marginLeft: 8 }}>{count}</Tag>}
                                        {hasAgentConfirm && <Badge status="processing" style={{ marginLeft: 5 }} />}
                                    </span>
                                ),
                                children: renderTransactionTypeTab(type.value),
                            };
                        }),
                        {
                            key: "newTransaction",
                            label: "发起新事务",
                            children: showActionForm ? (
                                <TransactionActionForm initialValues={formInitialValues} onCancel={handleFormCancel} onSubmit={handleFormSubmit} />
                            ) : (
                                <div style={{ padding: "20px 0" }}>
                                    <Space direction="vertical" style={{ width: "100%" }}>
                                        <Alert message="请选择要发起的事务类型" type="info" showIcon />
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "16px" }}>
                                            {transactionTypes.map((type) => (
                                                <Button
                                                    key={type.value}
                                                    type={selectedTransactionType === type.value ? "primary" : "default"}
                                                    icon={<span style={{ marginRight: "8px" }}>{type.icon}</span>}
                                                    onClick={() => {
                                                        setSelectedTransactionType(type.value);
                                                        setFormInitialValues({
                                                            flightInfo: flight,
                                                            transactionType: type.value,
                                                        });
                                                        setShowActionForm(true);
                                                    }}
                                                    style={{ minWidth: "140px" }}
                                                >
                                                    {type.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </Space>
                                </div>
                            ),
                        },
                    ]}
                />

                {/* 邮件预览抽屉 */}
                {renderEmailPreview()}
            </Modal>

            {/* Agent操作确认对话框 */}
            <Modal
                title={
                    <Space>
                        <RobotOutlined />
                        <span>确认AI助手处理建议</span>
                    </Space>
                }
                open={agentConfirmVisible}
                onCancel={() => {
                    if (!agentActionLoading) {
                        setAgentConfirmVisible(false);
                        setCurrentAgentTransaction(null);
                    }
                }}
                confirmLoading={agentActionLoading}
                footer={[
                    <Button key="reject" danger onClick={rejectAgentAction} loading={agentActionLoading}>
                        拒绝
                    </Button>,
                    <Button
                        key="view-email"
                        type="dashed"
                        onClick={() => {
                            if (currentAgentTransaction?.related_email_id) {
                                handleViewEmail(currentAgentTransaction.related_email_id);
                            }
                        }}
                        disabled={!currentAgentTransaction?.related_email_id}
                    >
                        查看原始邮件
                    </Button>,
                    <Button key="confirm" type="primary" onClick={confirmAgentAction} loading={agentActionLoading}>
                        确认
                    </Button>,
                ]}
                width={600}
            >
                {currentAgentTransaction && (
                    <>
                        <Alert
                            message="AI助手已解析邮件内容并提出处理建议"
                            description={
                                <>
                                    <p>根据邮件内容，AI助手分析出以下信息：</p>
                                </>
                            }
                            type="info"
                            showIcon
                            icon={<RobotOutlined />}
                            style={{ marginBottom: 16 }}
                        />

                        <Descriptions
                            bordered
                            size="small"
                            column={1}
                            title={
                                <Space>
                                    <span>事务信息</span>
                                    {currentAgentTransaction.agent_confidence && (
                                        <Tooltip title="AI助手对结果的确信程度">
                                            <Tag color="blue">置信度: {(currentAgentTransaction.agent_confidence * 100).toFixed(0)}%</Tag>
                                        </Tooltip>
                                    )}
                                </Space>
                            }
                        >
                            <Descriptions.Item label="事务类型">
                                <Tag color={typeColorMap[currentAgentTransaction.record_type] || "default"}>{currentAgentTransaction.record_type}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="事件描述">{currentAgentTransaction.event_description}</Descriptions.Item>
                            <Descriptions.Item label="检测时间">{dayjs(currentAgentTransaction.created_at).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
                            <Descriptions.Item label="关联邮件">
                                {currentAgentTransaction.related_email_id ? (
                                    <Button type="link" onClick={() => handleViewEmail(currentAgentTransaction.related_email_id)}>
                                        查看ID: {currentAgentTransaction.related_email_id}
                                    </Button>
                                ) : (
                                    "无"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="AI建议">
                                <Text strong>{currentAgentTransaction.agent_suggestion}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Text type="secondary">请确认AI助手的处理建议是否正确。确认后将按照建议处理该事务，拒绝则保持事务为待处理状态。</Text>

                            <Alert message="请注意" description="如果AI助手的处理建议不够明确或者有错误，您可以拒绝此建议并手动处理。" type="warning" showIcon style={{ marginTop: 12 }} />
                        </Space>
                    </>
                )}
            </Modal>
        </>
    );
};

const getStatusBadgeType = (status) => {
    switch (status) {
        case "COMPLETED":
            return "green";
        case "DELAYED":
            return "orange";
        case "CANCELLED":
            return "red";
        case "ACTIVE":
            return "purple";
        case "SCHEDULED":
            return "blue";
        default:
            return "default";
    }
};

// 事务类型颜色映射
const typeColorMap = {
    PERMIT: "blue",
    FUEL: "green",
    SLOT: "orange",
    HANDLING: "purple",
    ACTIVATION: "cyan",
    MVT: "geekblue",
    CREW: "magenta",
    CATERING: "gold",
};

export default TransactionModal;
